/**
 * Le stesse tre colonne del Cruscotto, con la **riga d'ordine** al posto della
 * commessa: articolo, quantità, e a fianco la commessa di riferimento.
 *
 * Perché esiste (Enrico, 23-09-2026): *«anche se una commessa non è totalmente
 * pronta può succedere che la merce sia spedita… i carichi possono essere creati
 * con più articoli di più commesse»*. Misurato prima di costruirla:
 *
 * - **91 commesse su 182** hanno righe in colonne diverse;
 * - **434 righe pronte — 46.193 pezzi, il 37% di tutto il pronto a magazzino —
 *   stanno dentro commesse classificate «non programmate»**, cioè merce che può
 *   partire oggi e che la vista per commessa nasconde;
 * - quando una nota di prelievo tocca una commessa, **14 volte su 20 ne prende
 *   solo una parte**.
 *
 * E c'è una ragione di sostanza, non solo di dettaglio: per la commessa serviva
 * una regola di aggregazione («il buco vince»), e ogni regola di aggregazione è
 * discutibile. **Una riga ha uno stato solo**: non c'è niente da decidere, e
 * quindi niente da contestare.
 */
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Truck } from "lucide-react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useM1RigheColonne } from "@/hooks/useM1";
import { useM3CreaCaricoDaRighe } from "@/hooks/useM3";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type { M1ChiaveColonna, M1ClienteRighe, M1ColonnaRighe, M1RigaInColonna } from "@/types/api";

const CLIENTI_VISIBILI = 10;
const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dataBreve(iso: string | null): string {
  if (!iso) return "—";
  const [, m, g] = iso.split("-").map(Number);
  return `${g} ${MESI[m - 1]}`;
}

const fmt = (v: string | number) => num(Number(v));

const COLONNE: Record<
  M1ChiaveColonna,
  { titolo: string; sotto: string; bordo: string; testa: string; numero: string }
> = {
  pronte: {
    titolo: "Pronti",
    sotto: "la merce è a magazzino: questi articoli possono partire oggi",
    bordo: "border-emerald-300 dark:border-emerald-900",
    testa: "bg-emerald-50 dark:bg-emerald-950/40",
    numero: "text-emerald-700 dark:text-emerald-400",
  },
  pianificate: {
    titolo: "In programmazione",
    sotto: "non ci sono ancora, ma la produzione è pianificata",
    bordo: "border-amber-300 dark:border-amber-900",
    testa: "bg-amber-50 dark:bg-amber-950/40",
    numero: "text-amber-700 dark:text-amber-400",
  },
  non_programmate: {
    titolo: "Non programmati",
    sotto: "nessuno li ha ancora messi in produzione",
    bordo: "border-rose-300 dark:border-rose-900",
    testa: "bg-rose-50 dark:bg-rose-950/40",
    numero: "text-rose-700 dark:text-rose-400",
  },
};

export function ColonneRighe({ attiva }: { attiva: boolean }) {
  const { data, isLoading } = useM1RigheColonne(attiva);
  // La selezione vive qui e non dentro la colonna: un carico si fa con righe di
  // **un cliente solo**, e tenerla in alto è ciò che permette di dirlo prima che
  // qualcuno provi.
  const [scelte, setScelte] = useState<Record<string, M1RigaInColonna & { cliente: number }>>({});

  if (isLoading) {
    return (
      <div className="grid gap-3 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }
  if (!data?.colonne.length) {
    return (
      <section className="rounded-lg border border-dashed bg-card/40 px-6 py-10 text-center">
        <p className="text-sm text-muted-foreground">Nessuna riga aperta.</p>
      </section>
    );
  }

  const scelti = Object.values(scelte);
  const clienteScelto = scelti.length ? scelti[0].cliente : null;

  const alterna = (riga: M1RigaInColonna, cliente: number) =>
    setScelte((prec) => {
      const n = { ...prec };
      if (n[riga.id]) delete n[riga.id];
      else n[riga.id] = { ...riga, cliente };
      return n;
    });

  return (
    <section className="space-y-2">
      <div className="grid items-start gap-3 lg:grid-cols-3">
        {data.colonne.map((col) => (
          <Colonna
            key={col.chiave}
            colonna={col}
            scelte={scelte}
            clienteScelto={clienteScelto}
            onAlterna={alterna}
          />
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground">
        {data.colonne.map((c) => fmt(c.n_righe)).join(" + ")} ={" "}
        <b className="text-foreground">{fmt(data.n_righe_aperte - data.n_righe_non_valutate)}</b>{" "}
        righe d&apos;ordine aperte. Ogni riga sta in una colonna sola: qui non c&apos;è nessuna
        regola di aggregazione, perché una riga ha uno stato solo.
      </p>

      {scelti.length > 0 && <BarraSelezione scelti={scelti} onSvuota={() => setScelte({})} />}
    </section>
  );
}

/** La barra che compare quando si è scelto qualcosa: cosa parte, e il gesto. */
function BarraSelezione({
  scelti,
  onSvuota,
}: {
  scelti: (M1RigaInColonna & { cliente: number })[];
  onSvuota: () => void;
}) {
  const crea = useM3CreaCaricoDaRighe();
  const [esito, setEsito] = useState<string | null>(null);
  const pallet = scelti.reduce((a, r) => a + (r.pallet ?? 0), 0);
  const pezzi = scelti.reduce((a, r) => a + Number(r.qty_residua), 0);
  const commesse = [...new Set(scelti.map((r) => r.numero_ordine))];
  const senzaPallet = scelti.filter((r) => r.pallet === null).length;

  return (
    <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-brand-600 bg-card px-3 py-2 shadow-lg">
      <span className="text-[12.5px]">
        <b>{scelti.length}</b> righe · <b>{fmt(pezzi)}</b> pezzi · <b>{pallet}</b> pallet ·{" "}
        {commesse.length} {commesse.length === 1 ? "commessa" : "commesse"}{" "}
        <span className="font-mono text-[11px] text-muted-foreground">
          #{commesse.join(" #")}
        </span>
      </span>
      {senzaPallet > 0 && (
        /* Un totale che non comprende tutto va detto, o il camion si pianifica
           su un numero più basso del vero. */
        <span className="text-[11px] text-amber-700 dark:text-amber-400">
          {senzaPallet} righe senza pezzi/pallet in anagrafica: non entrano nel totale
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {esito && <span className="text-[11.5px] text-muted-foreground">{esito}</span>}
        <button
          type="button"
          onClick={onSvuota}
          className="rounded px-2 py-1 text-[11.5px] hover:bg-muted"
        >
          Annulla
        </button>
        <button
          type="button"
          disabled={crea.isPending}
          onClick={() => {
            setEsito(null);
            crea.mutate(
              { righe_id: scelti.map((r) => r.id) },
              {
                onSuccess: (d) => {
                  setEsito(`Creato il carico di ${d.cliente_nome}`);
                  onSvuota();
                },
                onError: (e: unknown) =>
                  setEsito(e instanceof Error ? e.message : String(e)),
              },
            );
          }}
          className="inline-flex items-center gap-1.5 rounded bg-foreground px-2.5 py-1.5 text-[11.5px] font-medium text-background disabled:opacity-50"
        >
          {crea.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Truck className="size-3.5" />
          )}
          Crea carico in Spedizioni
        </button>
      </div>
    </div>
  );
}

function Colonna({
  colonna,
  scelte,
  clienteScelto,
  onAlterna,
}: {
  colonna: M1ColonnaRighe;
  scelte: Record<string, unknown>;
  clienteScelto: number | null;
  onAlterna: (riga: M1RigaInColonna, cliente: number) => void;
}) {
  const stile = COLONNE[colonna.chiave];
  const [tutti, setTutti] = useState(false);
  const clienti = tutti ? colonna.clienti : colonna.clienti.slice(0, CLIENTI_VISIBILI);
  const nascosti = colonna.clienti.length - clienti.length;

  return (
    <div className={cn("overflow-hidden rounded-lg border bg-card", stile.bordo)}>
      <div className={cn("border-b px-3 py-2", stile.testa, stile.bordo)}>
        <div className="flex items-baseline gap-2">
          <span className={cn("text-2xl font-semibold leading-none tabular-nums", stile.numero)}>
            {fmt(colonna.n_righe)}
          </span>
          <h3 className="text-[13px] font-semibold">{stile.titolo}</h3>
        </div>
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{stile.sotto}</p>
        <p className="mt-1 text-[10.5px] text-muted-foreground">
          {fmt(colonna.qty_residua)} pezzi · <b className="text-foreground">{fmt(colonna.pallet)}</b>{" "}
          pallet · {fmt(colonna.n_clienti)} {colonna.n_clienti === 1 ? "cliente" : "clienti"}
          {colonna.n_impegnate > 0 && <> · {fmt(colonna.n_impegnate)} già su una nota</>}
        </p>
      </div>

      {colonna.clienti.length === 0 ? (
        <p className="px-3 py-6 text-center text-[11.5px] text-muted-foreground">
          Nessuna riga in questa colonna.
        </p>
      ) : (
        <ul className="divide-y">
          {clienti.map((c) => (
            <RigaCliente
              key={c.cliente_codice}
              cliente={c}
              selezionabile={colonna.chiave === "pronte"}
              scelte={scelte}
              clienteScelto={clienteScelto}
              onAlterna={onAlterna}
            />
          ))}
        </ul>
      )}

      {nascosti > 0 && (
        <button
          type="button"
          onClick={() => setTutti(true)}
          className="w-full border-t px-3 py-1.5 text-[11px] text-brand-700 hover:bg-accent/40 dark:text-brand-400"
        >
          altri {fmt(nascosti)} clienti
        </button>
      )}
    </div>
  );
}

function RigaCliente({
  cliente,
  selezionabile,
  scelte,
  clienteScelto,
  onAlterna,
}: {
  cliente: M1ClienteRighe;
  selezionabile: boolean;
  scelte: Record<string, unknown>;
  clienteScelto: number | null;
  onAlterna: (riga: M1RigaInColonna, cliente: number) => void;
}) {
  const [aperto, setAperto] = useState(false);
  // Un carico va verso **un cliente solo**: le righe di un altro cliente si
  // spengono invece di far fallire il salvataggio dopo.
  const bloccato = clienteScelto !== null && clienteScelto !== cliente.cliente_codice;

  return (
    <li>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent/40"
        aria-expanded={aperto}
      >
        {aperto ? (
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
          {cliente.cliente_ragione_sociale}
        </span>
        {cliente.righe_non_attribuite > 0 && (
          /* Righe che una nota viva ha preso senza dire di quale commessa: non si
             marcano a caso — un impegno dimenticato fa caricare due volte la
             stessa merce — si dichiarano. */
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-300"
            title={`${cliente.righe_non_attribuite} righe di una nota di prelievo non dicono a quale commessa appartengono: controlla in Spedizioni prima di caricare`}
          >
            <AlertTriangle className="size-2.5" />
            {cliente.righe_non_attribuite}
          </span>
        )}
        <span
          className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-semibold tabular-nums"
          title={`${cliente.n_righe} righe in questa colonna, su ${cliente.n_righe_cliente} righe aperte`}
        >
          {cliente.n_righe}/{cliente.n_righe_cliente}
        </span>
        <span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
          {cliente.pallet ? `${fmt(cliente.pallet)} pal` : "—"}
        </span>
      </button>

      {aperto && (
        <ul className="border-t bg-muted/20">
          {cliente.righe.map((r) => (
            <Riga
              key={r.id}
              riga={r}
              selezionabile={selezionabile && !r.impegnata}
              bloccato={bloccato}
              scelta={!!scelte[r.id]}
              onAlterna={() => onAlterna(r, cliente.cliente_codice)}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function Riga({
  riga: r,
  selezionabile,
  bloccato,
  scelta,
  onAlterna,
}: {
  riga: M1RigaInColonna;
  selezionabile: boolean;
  bloccato: boolean;
  scelta: boolean;
  onAlterna: () => void;
}) {
  return (
    <li
      className={cn(
        "flex items-start gap-2 px-3 py-1.5 pl-8",
        scelta && "bg-brand-50 dark:bg-brand-950/40",
      )}
    >
      {selezionabile && (
        <input
          type="checkbox"
          checked={scelta}
          disabled={bloccato}
          onChange={onAlterna}
          className="mt-1 shrink-0 rounded disabled:opacity-30"
          title={
            bloccato
              ? "Hai già scelto righe di un altro cliente: un carico va verso un cliente solo"
              : "Metti questa riga sul carico"
          }
        />
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-mono text-[11px] font-semibold">{r.codice_articolo}</span>
          <span className="font-mono text-[10.5px] text-muted-foreground">#{r.numero_ordine}</span>
          {r.impegnata && (
            /* L'impegno è **di questa riga**, non della sua commessa: quando una
               nota tocca una commessa ne prende solo una parte 14 volte su 20, e
               marcare tutta la commessa direbbe «impegnate» a 115 righe libere. */
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 px-1.5 py-px text-[10px] font-medium text-sky-900 dark:bg-sky-950 dark:text-sky-300"
              title="Questa riga è già su una nota di prelievo: la merce è destinata a quel carico"
            >
              <Truck className="size-2.5" />
              NDP {r.ndp_numero ?? "emessa"}
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-[10.5px] leading-snug text-muted-foreground">
          {r.descrizione_articolo ?? "—"}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-[11.5px] font-medium tabular-nums">{fmt(r.qty_residua)}</span>
        <span className="block text-[10px] tabular-nums text-muted-foreground">
          {r.pallet !== null ? `${r.pallet} pal` : "pallet ?"}
          {r.pronta_il ? ` · ${dataBreve(r.pronta_il)}` : ""}
        </span>
      </span>
    </li>
  );
}
